import { useCallback, useRef, useState } from 'react';
import { createProductDirectoryApi } from './product-directory-api';
import type { ProductDirectoryEntry, ProductDirectoryResponse } from './product-directory-api';

export type ProductDirectoryState = {
  directory: ProductDirectoryResponse | null;
  favorites: string[];
  isLoading: boolean;
  hasFailed: boolean;
  load: () => Promise<void>;
  toggleFavorite: (product: string) => Promise<void>;
  reorderFavorites: (products: string[]) => Promise<void>;
  launch: (product: ProductDirectoryEntry) => Promise<void>;
};

type UseProductDirectoryOptions = {
  logoutUrl: string;
  navigate?: (url: string) => void;
};

/**
 * Holds the switcher's directory and the products starred in it. The directory is fetched on demand
 * rather than at mount so a shell that never opens the switcher pays nothing, and a favorite
 * is written optimistically and reverted when the save fails.
 */
export function useProductDirectory(options: UseProductDirectoryOptions): ProductDirectoryState {
  const [directory, setDirectory] = useState<ProductDirectoryResponse | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const apiRef = useRef(createProductDirectoryApi({ logoutUrl: options.logoutUrl }));
  const loadingRef = useRef(false);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!loadingRef.current && !loadedRef.current) {
      loadingRef.current = true;
      setIsLoading(true);
      setHasFailed(false);
      try {
        const loaded = await apiRef.current.getDirectory();
        // Every list is read straight into a render, so a body missing one blanks the whole
        // menu. A shape we cannot render is a failed load, not a half-drawn one.
        if (!Array.isArray(loaded.products) || !Array.isArray(loaded.categories)) {
          throw new Error('The product directory came back in a shape we cannot render');
        }
        setDirectory({
          current: loaded.current,
          products: loaded.products,
          categories: loaded.categories,
          favorites: Array.isArray(loaded.favorites) ? loaded.favorites : [],
          jobs: Array.isArray(loaded.jobs) ? loaded.jobs : [],
          applications: Array.isArray(loaded.applications) ? loaded.applications : [],
          shortcuts: Array.isArray(loaded.shortcuts) ? loaded.shortcuts : [],
        });
        setFavorites(Array.isArray(loaded.favorites) ? loaded.favorites : []);
        loadedRef.current = true;
      } catch {
        setHasFailed(true);
      }
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // The stored list is both the membership and the order, so starring and dragging are the
  // same write: show the new list, send it, and fall back to the one that is stored.
  const storeFavorites = useCallback(async (next: string[], previous: string[]) => {
    setFavorites(next);
    try {
      const stored = await apiRef.current.saveFavorites(next);
      setFavorites(stored);
    } catch {
      setFavorites(previous);
    }
  }, []);

  const toggleFavorite = useCallback(
    async (product: string) => {
      const previous = favorites;
      let next = previous.filter((name) => name !== product);
      if (!previous.includes(product)) {
        next = [...previous, product];
      }
      await storeFavorites(next, previous);
    },
    [favorites, storeFavorites]
  );

  const reorderFavorites = useCallback(
    async (products: string[]) => {
      await storeFavorites(products, favorites);
    },
    [favorites, storeFavorites]
  );

  const launch = useCallback(
    async (product: ProductDirectoryEntry) => {
      const url = product.url;
      if (typeof url === 'string' && url.length > 0) {
        if (options.navigate) {
          options.navigate(url);
        } else {
          window.location.assign(url);
        }
      }
    },
    [options]
  );

  return {
    directory: directory,
    favorites: favorites,
    isLoading: isLoading,
    hasFailed: hasFailed,
    load: load,
    toggleFavorite: toggleFavorite,
    reorderFavorites: reorderFavorites,
    launch: launch,
  };
}
